import { FormEvent, useEffect, useState } from "react";
import { api, NetworkStatus, PlatformSettings, session, SystemStatus } from "./api";
import { bytes } from "./format";
import { PageHeader } from "./ui";

export function SystemPanels() {
  const [status, setStatus] = useState<SystemStatus>();
  const [network, setNetwork] = useState<NetworkStatus>();
  const [error, setError] = useState("");
  useEffect(() => {
    api
      .systemStatus()
      .then(setStatus)
      .catch((e) => setError(e instanceof Error ? e.message : "Falha ao carregar as métricas."));
    api.networkStatus().then(setNetwork).catch(() => undefined);
  }, []);
  if (error) return <p className="muted">{error}</p>;
  if (!status) return <p className="muted">Carregando métricas do servidor…</p>;
  const server = status.server;
  const memoryUsed = server.memoryTotalBytes - server.memoryAvailableBytes;
  const diskUsed = server.diskTotalBytes - server.diskFreeBytes;
  const asPercent = (part: number, total: number) => (total > 0 ? `${Math.round((part / total) * 100)}%` : "—");
  const days = Math.floor(server.uptimeHours / 24);
  const hours = Math.round(server.uptimeHours % 24);
  return (
    <section className="settings-grid">
      <article className="panel settings-card">
        <h2>Servidor</h2>
        <dl className="system-metrics">
          <div><dt>Uptime</dt><dd>{days > 0 ? `${days}d ${hours}h` : `${hours}h`}</dd></div>
          <div><dt>Carga (1/5/15 min)</dt><dd>{server.load1.toFixed(2)} · {server.load5.toFixed(2)} · {server.load15.toFixed(2)} <small>({server.cpuCount} vCPU)</small></dd></div>
          <div><dt>Memória</dt><dd>{bytes(memoryUsed)} de {bytes(server.memoryTotalBytes)} ({asPercent(memoryUsed, server.memoryTotalBytes)})</dd></div>
          <div><dt>Disco</dt><dd>{bytes(diskUsed)} de {bytes(server.diskTotalBytes)} ({asPercent(diskUsed, server.diskTotalBytes)})</dd></div>
          <div><dt>Postgres</dt><dd>{server.databaseConnections} conexões · {bytes(server.databaseBytes)}</dd></div>
        </dl>
      </article>
      <article className="panel settings-card">
        <h2>Ingestão (última hora)</h2>
        <dl className="system-metrics">
          <div><dt>Equipamentos online</dt><dd>{status.ingestion.devicesOnline}</dd></div>
          <div><dt>Capturas</dt><dd>{status.ingestion.snapshotsLastHour}</dd></div>
          <div><dt>Eventos do Windows</dt><dd>{status.ingestion.eventsLastHour}</dd></div>
          <div><dt>Omissões</dt><dd>{status.ingestion.omissionsLastHour}</dd></div>
        </dl>
      </article>
      <article className="panel settings-card">
        <h2>Storage</h2>
        <dl className="system-metrics">
          <div><dt>Bucket</dt><dd>{status.storage.bucket}</dd></div>
          <div><dt>Endpoint</dt><dd className="break">{status.storage.endpoint}</dd></div>
          <div><dt>Objetos</dt><dd>{status.storage.objects}</dd></div>
          <div><dt>Total</dt><dd>{bytes(status.storage.bytes)}</dd></div>
        </dl>
      </article>

      <article className="panel settings-card">
        <h2>Firewall e IPs banidos</h2>
        {!network ? (
          <p className="muted">Carregando estado da rede…</p>
        ) : (
          <dl className="system-metrics">
            <div><dt>Firewall</dt><dd>{network.firewallActive ? `Ativo · ${network.firewallRules} regras` : "Sem regras ativas"}</dd></div>
            <div><dt>Login por senha (SSH)</dt><dd>{network.passwordAuthentication === "no" ? "Desativado" : network.passwordAuthentication}</dd></div>
            <div><dt>Login de root (SSH)</dt><dd>{network.permitRootLogin}</dd></div>
            <div><dt>Jails (fail2ban)</dt><dd>{network.jails.length ? network.jails.join(", ") : "Não instalado"}</dd></div>
            <div><dt>IPs banidos</dt><dd>{network.bannedIps.length ? network.bannedIps.join(", ") : "Nenhum"}</dd></div>
          </dl>
        )}
        {network && (
          <>
            <h3>Portas em escuta</h3>
            <ul className="muted">
              {network.ports.map((port) => <li key={port} className="break">{port}</li>)}
            </ul>
            <p className="muted">Apenas leitura. Banir/desbanir exige um fluxo auditado próprio.</p>
          </>
        )}
      </article>
    </section>
  );
}

export function SettingsPage() {
  const isPlatformAdmin = session.user()?.platformRole === "platform_admin";
  const [settings, setSettings] = useState<PlatformSettings>();
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (isPlatformAdmin) api.platformSettings().then(setSettings).catch((e) => setError(e.message));
  }, [isPlatformAdmin]);
  const save = async (event: FormEvent) => {
    event.preventDefault();
    if (!settings) return;
    setBusy(true); setError(""); setMessage("");
    try { await api.updatePlatformSettings(settings); setMessage("Configurações salvas e registradas na auditoria."); setSettings(await api.platformSettings()); }
    catch (e) { setError(e instanceof Error ? e.message : "Falha ao salvar configurações."); }
    finally { setBusy(false); }
  };
  return <>
    <PageHeader eyebrow="PREFERÊNCIAS E OPERAÇÃO" title="Configurações" subtitle="Personalize o painel e os padrões operacionais da plataforma." />
    {!!session.user()?.platformRole && <SystemPanels />}
    {error && <p className="error">{error}</p>}{message && <p className="success notice">{message}</p>}
    <section className="settings-grid">
      <article className="panel settings-card"><h2>Aparência</h2><p className="muted">O tema agora pode ser alternado pelo botão fixo no topo do painel.</p></article>
      {isPlatformAdmin && settings && <article className="panel settings-card"><h2>Operação da plataforma</h2><p className="muted">Limites globais protegidos por auditoria.</p><form onSubmit={save}><label>Retenção da auditoria (dias)<input type="number" min="30" max="3650" value={settings.auditRetentionDays} onChange={(e) => setSettings({ ...settings, auditRetentionDays: Number(e.target.value) })} /></label><label>Intervalo da limpeza (horas)<input type="number" min="1" max="24" value={settings.retentionIntervalHours} onChange={(e) => setSettings({ ...settings, retentionIntervalHours: Number(e.target.value) })} /></label><label>Validade padrão das chaves (horas)<input type="number" min="1" max="720" value={settings.activationKeyDefaultHours} onChange={(e) => setSettings({ ...settings, activationKeyDefaultHours: Number(e.target.value) })} /></label><label>Máximo do acesso de suporte (horas)<input type="number" min="1" max="168" value={settings.supportAccessMaxHours} onChange={(e) => setSettings({ ...settings, supportAccessMaxHours: Number(e.target.value) })} /></label><label>Backup diário do banco<select value={settings.backupEnabled ? "1" : "0"} onChange={(e) => setSettings({ ...settings, backupEnabled: e.target.value === "1" })}><option value="1">Ativado</option><option value="0">Desativado</option></select></label><label>Hora do backup (UTC)<input type="number" min="0" max="23" value={settings.backupHour} onChange={(e) => setSettings({ ...settings, backupHour: Number(e.target.value) })} /></label><label>Retenção do backup (dias)<input type="number" min="1" max="90" value={settings.backupRetentionDays} onChange={(e) => setSettings({ ...settings, backupRetentionDays: Number(e.target.value) })} /></label>{settings.backupLastStatus && <small className="muted">Último backup: {settings.backupLastStatus === "ok" ? "concluído" : settings.backupLastStatus}{settings.backupLastRunAt ? ` em ${new Date(settings.backupLastRunAt).toLocaleString("pt-BR")}` : ""}</small>}<label className="check support-enable"><input type="checkbox" checked={settings.supportWidgetEnabled} onChange={(e) => setSettings({ ...settings, supportWidgetEnabled: e.target.checked })} /> Ativar chat de suporte</label><label className="support-html">HTML do chat de suporte<textarea maxLength={50000} rows={12} value={settings.supportWidgetHtml} onChange={(e) => setSettings({ ...settings, supportWidgetHtml: e.target.value })} /><small>Executado em iframe isolado, sem acesso ao token ou ao painel.</small></label><button disabled={busy}>{busy ? "Salvando…" : "Salvar configurações"}</button></form></article>}
    </section>
  </>;
}


